import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { AutoPaginate } from "../../pageComponents/BillingReportVersion2";
import styles from "../../components/Report/BillingReportVersion2.module.css";
import {
  useGetAdditionalNotesPage,
  useGetBarriersPdfPage,
  useGetBillingCyclePdfPage,
  useGetBillingDataSummaryPdfPage,
  useGetPatientReportedOutcomesPdfPage,
} from "../../hooks/RTMBillingReportPage.tsx";

const RTMBillingReportPage: React.FC<{ customMargin?: string | number }> = ({
  customMargin,
}) => {
  const { billingId } = useParams<{ billingId: string }>();
  const [summary1Pages, setSummary1Pages] = useState(0);
  const [summary2Pages, setSummary2Pages] = useState(0);
  const [summary3Pages, setSummary3Pages] = useState(0);

  const { billingDetails } = useGetBillingCyclePdfPage({
    billingId: billingId,
  });

  const { getAllNotesLoading, additionalNotes } = useGetAdditionalNotesPage({
    billingId: billingId,
  });

  const { dataSummary } = useGetBillingDataSummaryPdfPage({
    billingId: billingId,
  });

  const { reportOutcome } = useGetPatientReportedOutcomesPdfPage({
    billingId: billingId,
  });

  const { getAllDataLoading, barriersData } = useGetBarriersPdfPage({
    billingId: billingId,
  });

  const formatDate = (dateString?: string | null) => {
    if (dateString === "NA") return "N/A";
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div>
      <AutoPaginate
        startPage={1}
        onPagesCalculated={setSummary1Pages}
        customMargin={customMargin}
        isModal={false}
      >
        <div>
          {" "}
          <div className={styles.flexLine}>
            <img
              src="/assets/logo-dark.png"
              alt="logo"
              className={styles.logoLeft}
            />
            <img
              src="/assets/cliniqLogo.png"
              alt="cliniQ logo"
              className={styles.logoRight}
            />
          </div>
          <div className={styles.fullWidthLine}></div>
          <div className={styles.reportContainer}>
            <h2 className={styles.centerHeading}>RTM Billing Report</h2>

            <div className={styles.patientInfoGrid}>
              <div className={styles.boldText}>Patient Name:</div>
              <div className={styles.normalText}>
                {billingDetails?.data.data.name || "N/A"}
              </div>

              <div className={styles.boldText}>Date of Birth:</div>
              <div className={styles.normalText}>
                {" "}
                {billingDetails?.data.data.dob || "N/A"}
              </div>

              <div className={styles.boldText}>RTM Platform Used:</div>
              <div className={styles.normalText}>
                Therapy Engagement Software: Perspectives in Care by Sure Med
                Compliance {billingDetails?.data.rtm_billing_report_version}.
              </div>

              <div className={styles.boldText}>Monitoring Start Date:</div>
              <div className={styles.normalText}>
                {(() => {
                  const consentDate = billingDetails?.data.data.consentDate;
                  const startDate =
                    billingDetails?.data.data.billingCycleStartDate;

                  if (!consentDate || !startDate) return startDate || "N/A";

                  const consent = new Date(consentDate);
                  const start = new Date(startDate);

                  const finalStartDate =
                    start < consent ? consentDate : startDate;

                  return finalStartDate;
                })()}
              </div>

              <div className={styles.boldText}>Consent Date:</div>
              <div className={styles.normalText}>
                {billingDetails?.data.data.consentDate || "N/A"}
              </div>
            </div>

            <p className={styles.introParagraph}>
              • Patient has an established diagnosis requiring ongoing
              monitoring of therapy adherence and/or therapeutic response.
              Remote Therapeutic Monitoring services are medically necessary to
              support behavior-based treatment interventions, improve
              compliance, and guide clinical decision-making. The patient has
              consented to participate in RTM.
            </p>

            <div className={styles.communicationSection}>
              <div>
                <strong className={styles.boldText}>
                  Interactive Communication:
                </strong>{" "}
                <span className={styles.normalText}>Phone</span>
              </div>
              <div>
                <strong className={styles.boldText}>
                  RTM Software Confirms 16 Days of Data Capture in Calendar
                  Month:
                </strong>{" "}
                <span className={styles.normalText}>
                  {(
                    billingDetails?.data.data.transmissionsCompletedStatus ||
                    "N/A"
                  )
                    .toLowerCase()
                    .replace(/^\w/, (c) => c.toUpperCase())}
                </span>
              </div>
              <div>
                <strong className={styles.boldText}>Last Transmission:</strong>{" "}
                <span className={styles.normalText}>
                  {formatDate(
                    billingDetails?.data?.data?.lastTransmissionDate
                  ) || "N/A"}
                </span>
              </div>
              <div>
                <strong className={styles.boldText}>Total Time Spent:</strong>{" "}
                <span className={styles.normalText}>
                  {" "}
                  {billingDetails?.data.data.consolidatedBillingMinutes}m
                </span>
              </div>
            </div>
            <div>
              <h3 className={styles.sectionHeading}>
                Therapy Type Being Monitored and Data Summary
              </h3>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <h4 className={styles.subHeading}>• Medication Adherence:</h4>
              <ul className={styles.listStyle}>
                <li>
                  Patient{" "}
                  {dataSummary?.hasPatientAdheredPrescribedPlan === "Yes"
                    ? "has"
                    : "has not"}{" "}
                  adhered to the current prescribed plan.{" "}
                  {dataSummary?.patientAdheredPrescribedPlanNotes && (
                    <>
                      Patient reported non-adherence because.{" "}
                      {dataSummary.patientAdheredPrescribedPlanNotes}. This was
                      discussed at visit.
                    </>
                  )}
                </li>

                {dataSummary?.medicationNotes &&
                  dataSummary.medicationNotes.length > 0 && (
                    <li>
                      <ul className={styles.notesList}>
                        {getAllDataLoading ? (
                          <li className={styles.normalText}>
                            Loading notes...
                          </li>
                        ) : (
                          dataSummary?.medicationNotes.map((note) => (
                            <li key={note._id}>
                              <div className={styles.allNote}></div>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: note.richText,
                                }}
                                className={styles.normalText}
                              />
                            </li>
                          ))
                        )}
                      </ul>
                    </li>
                  )}
              </ul>
            </div>
          </div>
        </div>
      </AutoPaginate>
      <AutoPaginate
        startPage={1 + summary1Pages}
        onPagesCalculated={setSummary2Pages}
        customMargin={customMargin}
        isModal={false}
      >
        {" "}
        <div className={styles.reportContainer}>
          <h4 className={styles.subHeadingAlt}>• Treatment Plan Adherence:</h4>
          <ul className={styles.listStyle}>
            <li>
              Physician required therapy: Patient{" "}
              {dataSummary?.hasPhysicianTherapyRequired === "Yes"
                ? "has"
                : "has not"}{" "}
              been required to complete additional therapy.
            </li>
            {dataSummary?.hasPhysicianTherapyRequired === "Yes" && (
              <li>
                <ul className={styles.ul}>
                  {dataSummary?.listOfTherapies?.length > 0 ? (
                    dataSummary?.listOfTherapies.map((t, i) => (
                      <li key={i} className={styles.normalText}>
                        {t.therapy} - {t.therapy_details}
                      </li>
                    ))
                  ) : (
                    <li className={styles.normalText}>No therapies reported</li>
                  )}
                </ul>
              </li>
            )}

            {dataSummary?.discussAdditionalTreatment &&
              dataSummary?.discussAdditionalTreatment?.length > 0 && (
                <li>
                  Patient has reported that they would like to discuss the
                  following additional treatment options at the next
                  appointment:
                  <ul className={styles.List}>
                    {dataSummary.discussAdditionalTreatment.map(
                      (treatment, i) => (
                        <li key={i} className={styles.normalText}>
                          {treatment}
                        </li>
                      )
                    )}
                  </ul>
                </li>
              )}

            {dataSummary?.treatmentPlanNotes &&
              dataSummary.treatmentPlanNotes.length > 0 && (
                <li>
                  <ul className={styles.notesList}>
                    {getAllDataLoading ? (
                      <li className={styles.normalText}>Loading notes...</li>
                    ) : (
                      dataSummary?.treatmentPlanNotes.map((note) => (
                        <li key={note._id}>
                          <div className={styles.allNote}></div>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: note.richText,
                            }}
                            className={styles.normalText}
                          />
                        </li>
                      ))
                    )}
                  </ul>
                </li>
              )}
          </ul>
          <div>
            <h4 className={styles.subHeadingAlt}>
              • Patient Therapeutic Goals:
            </h4>
            <ul className={styles.listStyle}>
              <li>
                Patient has reported the following activities of daily living
                (ADL) goals:
                <ul className={styles.ul}>
                  {dataSummary?.hasListOfGoalDiscussed &&
                    dataSummary.hasListOfGoalDiscussed
                      .toLowerCase()
                      .includes("yes") &&
                    dataSummary?.listOfGoalDiscussed && (
                      <li className={styles.normalText}>
                        {dataSummary.listOfGoalDiscussed}
                      </li>
                    )}
                </ul>
              </li>

              {dataSummary?.goalsNotes && dataSummary.goalsNotes.length > 0 && (
                <li>
                  <ul className={styles.notesList}>
                    {getAllDataLoading ? (
                      <li className={styles.normalText}>Loading notes...</li>
                    ) : (
                      dataSummary?.goalsNotes.map((note) => (
                        <li key={note._id}>
                          <div className={styles.allNote}></div>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: note.richText,
                            }}
                            className={styles.normalText}
                          />
                        </li>
                      ))
                    )}
                  </ul>
                </li>
              )}
            </ul>
          </div>
        </div>
      </AutoPaginate>
      <AutoPaginate
        startPage={1 + summary1Pages + summary2Pages}
        onPagesCalculated={setSummary3Pages}
        customMargin={customMargin}
        isModal={false}
      >
        {" "}
        <div className={styles.reportContainer}>
          <div>
            <h4 className={styles.subHeadingAlt}>
              • Side Effect/ Adverse Event Reporting:
            </h4>
            <ul className={styles.listStyle}>
              <li>
                Patient has reported side effects with current therapy.
                <ul className={styles.ul}>
                  {dataSummary?.hasSideEffect &&
                    dataSummary.hasSideEffect.toLowerCase() === "yes" &&
                    dataSummary?.sideEffectList && (
                      <li className={styles.normalText}>
                        {dataSummary.sideEffectList}
                      </li>
                    )}
                </ul>
              </li>

              {dataSummary?.sideEffectTransmissionList &&
                dataSummary?.sideEffectTransmissionList?.length > 0 && (
                  <li>
                    <ul className={styles.ul}>
                      {dataSummary.sideEffectTransmissionList.map(
                        (effect, i) => (
                          <li key={i} className={styles.normalText}>
                            {effect}
                          </li>
                        )
                      )}
                    </ul>
                  </li>
                )}

              {dataSummary?.sideEffectNotes &&
                dataSummary.sideEffectNotes.length > 0 && (
                  <li>
                    <ul className={styles.notesList}>
                      {getAllDataLoading ? (
                        <li className={styles.normalText}>Loading notes...</li>
                      ) : (
                        dataSummary?.sideEffectNotes.map((note) => (
                          <li key={note._id}>
                            <div className={styles.allNote}></div>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: note.richText,
                              }}
                              className={styles.normalText}
                            />
                          </li>
                        ))
                      )}
                    </ul>
                  </li>
                )}
            </ul>
          </div>

          <div>
            <h3 className={styles.sectionHeadingAlt}>
              Patient Reported Outcomes
            </h3>
          </div>
          <ul className={styles.listWithTopMargin}>
            <li className={styles.marginBottomSmall}>
              Patient scored {reportOutcome?.pegScore || "0"} on the PEG scale.
            </li>
            <li className={styles.marginBottomSmall}>
              Compared to baseline, patient has reported a{" "}
              {reportOutcome?.pain || "-"}% {reportOutcome?.painStatus} in pain.
            </li>
            <li className={styles.marginBottomSmall}>
              Patient scored {reportOutcome?.pdiScore || "-"} on the Pain
              Disability Index Scale.
            </li>
            <li className={styles.marginBottomSmall}>
              {reportOutcome?.depression === "yes" &&
              reportOutcome?.anxiety === "yes"
                ? "Patient is reporting both depression and anxiety, as assessed by PHQ2/DSM-5 criteria."
                : reportOutcome?.depression === "yes"
                ? "Patient is reporting depression, as assessed by PHQ2/DSM-5 criteria."
                : reportOutcome?.anxiety === "yes"
                ? "Patient is reporting anxiety, as assessed by PHQ2/DSM-5 criteria."
                : "Patient is not reporting depression or anxiety, as assessed by PHQ2/DSM-5 criteria."}
            </li>
          </ul>

          <div>
            <h3
              className={styles.sectionHeadingWithMargin}
              style={{ marginTop: "2rem" }}
            >
              Barriers
            </h3>
            {getAllDataLoading ? (
              <ul className={styles.listWithTopMargin}>
                <li className={styles.normalText}>Loading barriers...</li>
              </ul>
            ) : barriersData && barriersData.length > 0 ? (
              <ul className={styles.listWithTopMargin}>
                {barriersData?.map((barrier) => (
                  <li key={barrier._id} style={{ marginBottom: "1rem" }}>
                    <div className={styles.noteHeader}></div>
                    <div
                      dangerouslySetInnerHTML={{ __html: barrier.richText }}
                      className={styles.normalText}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <ul className={styles.listWithTopMargin}>
                <li className={styles.normalText}>
                  No barriers reported for this Billing cycle.
                </li>
              </ul>
            )}
          </div>
        </div>
      </AutoPaginate>

      <AutoPaginate
        startPage={1 + summary1Pages + summary2Pages + summary3Pages}
        customMargin={customMargin}
        isModal={false}
      >
        <div>
          <h3 className={styles.sectionHeadingWithMargin}>Additional Notes</h3>
          {getAllNotesLoading ? (
            <div className={styles.normalText} style={{ marginTop: "2rem" }}>
              Loading notes...
            </div>
          ) : additionalNotes && additionalNotes.length > 0 ? (
            <ul className={styles.listWithTopMargin}>
              {additionalNotes?.map((note) => (
                <li key={note._id} style={{ marginBottom: "1rem" }}>
                  <div className={styles.noteHeader}></div>
                  <div
                    dangerouslySetInnerHTML={{ __html: note.richText }}
                    className={styles.normalText}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <ul className={styles.listWithTopMargin}>
              <li>No notes available for this Billing cycle.</li>
            </ul>
          )}
        </div>
      </AutoPaginate>
    </div>
  );
};

export default RTMBillingReportPage;
